import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faAngleLeft, faPrint } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import getCDNURL from '@pickleballinc/ui/components/global/utils/getCDNURL';
import clsx from 'clsx';
import { useParams, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { MatchupStatusBadge } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/MatchupStatusBadge';
import { Button } from '@/components/atoms';
import { DreamBreakerBox } from '@/components/molecules';
import { MlpScoreSheetPrint, OfficialScoreSheetPrint, PrintMatchesModal, SimplifiedScoreSheetPrint } from '@/components/molecules/prints';
import { CompleteMatchesModal, CompleteMatchupModal } from '@/components/molecules/ScoreCards/modals';
import { EditMatchupStatusModal } from '@/components/organisms/ScoreCards/EditMatchupStatusModal';
import {
	getMatchupStatusApiValue,
	MatchCompletedEnum,
	MatchStatusApiMap,
	MatchStatusEnum,
	MatchupCompletedEnum,
	MatchupStatusEnum
} from '@/domain/constants/constants';
import { Matches } from '@/entities';
import { PermissionRole } from '@/entities/PermissionRoles';
import { SystemLocationsCourts } from '@/entities/SystemLocationsCourts';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { useTieBreakerMatch } from '@/hooks/tie-breaker/useTieBreakerMatch';
import { useRotationsInfoQuery } from '@/hooks/tie-breaker/useTieBreakerRotationsInfo';
import { isMatchTied } from '@/infrastructure/utils/tieBreakerHelpers';
import { usePermissionStore } from '@/store/permission-store';
import { useTiebreakerStore } from '@/store/tie-breaker-store';
import { DateUtil } from '@/utils/date';
import { PodsResponse } from '@/utils/types';
import { env } from '@env';

interface Props {
	matchup: Matches;
	canEditLineups: boolean;
	previousPageTitle: string;
	isTieBreaker?: boolean;
	isLineupPage?: boolean;
	podsResponse?: PodsResponse;
	systemLocationsCourts?: SystemLocationsCourts[];
}

export const Flag = ({ isWinner, result }: { isWinner: boolean; result: number }) => {
	return (
		<div
			className={clsx('w-14 h-20 md:w-20 md:h-28 bg-orange-500 flex items-center justify-center text-white text-4xl font-bold', {
				'bg-[url(/assets/images/winner-background.svg)]': isWinner
			})}
			style={{
				clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 90%, 0 100%)'
			}}
		>
			{result}
		</div>
	);
};

const TeamInfo = ({
	title,
	ranking,
	side,
	matchupStatus,
	pointsDiffPct,
	className
}: {
	title: string;
	ranking: string | number | null;
	side: 'Home' | 'Away';
	matchupStatus: MatchupStatusEnum;
	pointsDiffPct: number;
	className?: string;
}) => {
	return (
		<div className={className ?? ''}>
			<p className="font-bold text-orange-500 text-sm md:text-2xl">{title}</p>
			<div className="flex w-full justify-between">
				<p className="text-gray-500 text-sm font-medium">#{ranking}</p>
				<p className="text-gray-500 text-sm font-medium">{side}</p>
			</div>
			{matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS || matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ? (
				<p className="text-gray-500 text-sm font-medium">PD%: {pointsDiffPct}</p>
			) : null}
			{/* <p>{matchup.teamTwoRating}</p> */} {/* Team Rating Missing */}
		</div>
	);
};

export const FlagsBanner = ({ matchup }: { matchup: Matches }) => {
	return (
		<div className="w-full border-t-2 border-orange-500 flex flex-col items-center sm:flex-row sm:items-start sm:justify-center gap-6 xl:justify-between xl:items-center">
			{/* Away Team */}
			<div className="hidden sm:flex sm:pt-2 xl:pt-0 flex-col flex-1 items-end xl:items-start">
				<img
					src={
						matchup.teamTwoLogo
							? getCDNURL(matchup.teamTwoLogo.url, 260, 160)
							: `${env.NEXT_PUBLIC_IMAGE_CDN}/placeholder/pickleball_transparent_square.png`
					}
					className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
					alt={matchup.teamTwoTitle}
				/>
				<TeamInfo
					title={`${matchup.teamTwoTitle}`}
					ranking={matchup.teamTwoRanking}
					side="Away"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamTwoPointsDiffPct}
					className="block xl:hidden"
				/>
			</div>

			{/* Center Part */}
			<div className="flex xl:items-center gap-6 flex-0">
				<TeamInfo
					title={matchup.teamTwoTitle}
					ranking={matchup.teamTwoRanking}
					side="Away"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamTwoPointsDiffPct}
					className="hidden xl:block"
				/>

				<div className="flex">
					<Flag isWinner={matchup.winner === 2} result={matchup.teamTwoScore} />
					<div className="w-12 h-20 md:w-16 md:h-28 bg-gray-900 flex items-center justify-center text-white text-sm">VS</div>
					<Flag isWinner={matchup.winner === 1} result={matchup.teamOneScore} />
				</div>
				<TeamInfo
					title={matchup.teamOneTitle}
					ranking={matchup.teamOneRanking}
					side="Away"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamOnePointsDiffPct}
					className="hidden xl:block"
				/>
			</div>

			{/* Home Team */}
			<div className="hidden sm:flex sm:pt-2 xl:pt-0 flex-col flex-1 items-start xl:items-end">
				<img
					src={
						matchup.teamOneLogo
							? getCDNURL(matchup.teamOneLogo.url, 260, 160)
							: `${env.NEXT_PUBLIC_IMAGE_CDN}/placeholder/pickleball_transparent_square.png`
					}
					className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
					alt={matchup.teamTwoTitle}
				/>
				<TeamInfo
					title={matchup.teamOneTitle}
					ranking={matchup.teamOneRanking}
					side="Away"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamOnePointsDiffPct}
					className="block xl:hidden"
				/>
			</div>

			{/* mobile only */}
			<div className="sm:hidden flex gap-6">
				<div className="flex flex-col">
					<img
						src={
							matchup.teamTwoLogo
								? getCDNURL(matchup.teamTwoLogo.url, 260, 160)
								: `${env.NEXT_PUBLIC_IMAGE_CDN}/placeholder/pickleball_transparent_square.png`
						}
						className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
						alt={matchup.teamTwoTitle}
					/>
					<TeamInfo
						title={`${matchup.teamTwoTitle}`}
						ranking={matchup.teamTwoRanking}
						side="Away"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamTwoPointsDiffPct}
						className="self-end"
					/>
				</div>
				<div className="flex flex-col">
					<img
						src={
							matchup.teamOneLogo
								? getCDNURL(matchup.teamOneLogo.url, 260, 160)
								: `${env.NEXT_PUBLIC_IMAGE_CDN}/placeholder/pickleball_transparent_square.png`
						}
						className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
						alt={matchup.teamTwoTitle}
					/>
					<TeamInfo
						title={matchup.teamOneTitle}
						ranking={matchup.teamOneRanking}
						side="Away"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamOnePointsDiffPct}
						className="self-start"
					/>
				</div>
			</div>
		</div>
	);
};

export const MatchupHeader = ({
	matchup,
	isTieBreaker = false,
	canEditLineups,
	podsResponse,
	previousPageTitle,
	isLineupPage,
	systemLocationsCourts
}: Props) => {
	const params = useParams<{
		teamLeagueId: string;
		matchupId: string;
	}>();
	const router = useRouter();
	const permissionStore = usePermissionStore();

	const scrollToTiebreaker = useTiebreakerStore((state) => state.scrollToTiebreaker);
	const { ianaTzIdentifier, abbreviation } = matchup.timezone;
	const matchupStartDate = DateUtil.formatUTCInTimezone(matchup.formattedStartDate, 'MMM D, YYYY | hh:mm A', ianaTzIdentifier);

	const showPrintButton = !isTieBreaker;
	const isTie = isMatchTied(matchup);

	const { status: tieBreakerMatchStatus, match: tieBreakerMatch, tieBreakerTitle, matchCompletedType } = useTieBreakerMatch(matchup);
	const { data: rotationsInfo } = useRotationsInfoQuery(tieBreakerMatch?.matchUuid ?? '');
	const isMatchupCompleted = matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;
	const isTieBreakerActive = tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.IN_PROGRESS_MATCH_STATUS];
	const isTieBreakerCompleted = tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS];

	const isTieBreakerMatchCompleted =
		tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] &&
		matchCompletedType === MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE;
	const showCompleteMatchesButton = !isMatchupCompleted && permissionStore.gamedayAccess && !isTieBreakerActive;

	const isMatchupCancelledOrWithdrawn =
		(matchup.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchup.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchup.matchupCompletedType === MatchupCompletedEnum.CANCELED_MATCHUP_COMPLETION_TYPE) &&
		matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;

	const showLineupsButton =
		!isMatchupCancelledOrWithdrawn &&
		canEditLineups &&
		!isTieBreakerActive &&
		!isTieBreakerCompleted &&
		!(
			matchup.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
			matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS
		);

	const showSubstituteButton =
		!isMatchupCancelledOrWithdrawn && permissionStore.gamedayAccess && matchup.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS;

	const incompleteMatches = matchup.matches.filter(
		(match) => match.matchStatus !== MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] && !match.isTieBreaker
	);

	const primaryButtonClasses =
		'font-semibold text-sm bg-darkorange-400 hover:bg-orange-500 text-white rounded-md text-center whitespace-nowrap w-full';
	const secondaryButtonClasses =
		'h-7 md:h-10 text-xs md:text-sm hover:bg-gray-50 bg-white border font-semibold text-sm rounded-md border-gray-300 text-center w-full';

	const [isEditMatchupModalOpen, setIsEditMatchupModalOpen] = useState(false);
	const [showPrintModal, setShowPrintModal] = useState(false);
	const [isCompleteMatchesModalOpen, setIsCompleteMatchesModalOpen] = useState<boolean>(false);
	const [isCompleteMatchupModalOpen, setIsCompleteMatchupModalOpen] = useState<boolean>(false);
	const [selectedPrintOptions, setSelectedPrintOptions] = useState<{
		sheetType: 'official' | 'simplified' | 'mlp';
		sheetSize: 'half' | 'full';
	}>({
		sheetType: 'official',
		sheetSize: 'half'
	});
	const contentRef = useRef<HTMLDivElement>(null);

	const updateMatchupMutation = useUpdateMatchup();

	const handleUndoAction = () => {
		updateMatchupMutation.mutate(
			{
				matchup_uuid: matchup.uuid,
				matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.READY_TO_BE_STARTED_STATUS)
			},
			{
				onSuccess: () => {
					setIsEditMatchupModalOpen(false);
				}
			}
		);
	};

	const handlePrintModalOpen = () => {
		setShowPrintModal(true);
	};

	const handleCompleteMatchesClick = () => {
		if (incompleteMatches.length > 0) {
			setIsCompleteMatchesModalOpen(true);
		} else {
			setIsCompleteMatchupModalOpen(true);
		}
	};

	const handleLineupsNavigation = () => {
		router.push(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}/lineups`);
	};

	const handleSubstituteNavigation = () => {
		router.push(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}/substitution`);
	};

	const generatePrintFilename = () => {
		const today = new Date();
		const dateStr = today.toLocaleDateString('en-CA').replace(/-/g, '');

		const eventTitle = matchup.seasonTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'TeamLeague';
		const sheetType = selectedPrintOptions.sheetType.charAt(0).toUpperCase() + selectedPrintOptions.sheetType.slice(1);
		const sheetSize = selectedPrintOptions.sheetSize === 'half' ? 'HalfSheet' : 'FullSheet';

		return `${eventTitle}_${sheetType}_${sheetSize}_${dateStr}.pdf`;
	};

	const reactToPrintFn = useReactToPrint({
		contentRef,
		documentTitle: generatePrintFilename()
	});

	const handlePrintWithOptions = (data: { sheetType: 'official' | 'simplified' | 'mlp'; sheetSize: 'half' | 'full' }) => {
		setSelectedPrintOptions(data);

		// Trigger print after a short delay to allow component to render
		setTimeout(() => {
			reactToPrintFn();
		}, 100);
	};

	const handleBackNavigation = () => {
		router.back();
	};

	const dreamBreakerProps = {
		courts: systemLocationsCourts ?? [],
		match: {
			matchUuid: tieBreakerMatch?.matchUuid ?? '',
			courtUuid: tieBreakerMatch?.courtUuid ?? '',
			matchAssignedToCourt: tieBreakerMatch?.matchAssignedToCourt ?? '',
			matchPlannedStart: tieBreakerMatch?.matchPlannedStart ?? ''
		},
		moduleUuid: matchup.matches[0]?.moduleUuid ?? '',
		locationUuid: matchup.locationUuid ?? ''
	};

	return (
		<div>
			<div className="flex flex-col w-full">
				<div className="mb-2 sm:mb-6 flex h-6 items-center gap-2 print-hidden">
					<button
						onClick={handleBackNavigation}
						className="flex items-center gap-2 text-gray-900 hover:text-gray-700 hover:underline bg-transparent border-none cursor-pointer"
					>
						<FontAwesomeIcon icon={faAngleLeft} className="size-5" />
						<span className="text-base font-medium leading-6">{previousPageTitle}</span>
					</button>
				</div>
				<div className="flex justify-between items-center bg-[#FEFAF5] rounded-t-lg p-4 border border-orange-50 ">
					{/* Matchup info */}
					<div className="flex flex-col gap-2">
						<MatchupStatusBadge matchupStatus={matchup.matchupStatus} />
						<p className="font-semibold text-base text-gray-700">
							{matchupStartDate} {abbreviation}
						</p>
						{matchup.venue ? (
							<div className="flex items-center gap-2">
								<FontAwesomeIcon icon={faLocationDot} className="size-4 text-gray-500" />
								<p className="text-sm font-medium text-gray-500">{matchup.venue}</p>
							</div>
						) : null}
						{matchup.courts ? <p className="text-sm font-medium text-gray-500">{matchup.courts}</p> : null}
					</div>

					{/* Matchup buttons */}
					<div className="flex flex-col gap-2">
						{matchup.isTeamLeagueTournamentMatchup ? (
							<a href={`${env.NEXT_PUBLIC_TLBRACKETS_URL}/team-leagues/${params.teamLeagueId}/event/${matchup.poolUuid}`}>
								<Button onClick={() => {}} className={secondaryButtonClasses}>
									View Bracket
								</Button>
							</a>
						) : null}
						{!!permissionStore.gamedayAccess && (
							<Button onClick={() => setIsEditMatchupModalOpen(true)} className={secondaryButtonClasses}>
								Edit Status
							</Button>
						)}
					</div>
				</div>
				<FlagsBanner matchup={matchup} />
				{/* Action Buttons */}
				{!isLineupPage && !isTieBreaker ? (
					<div className="w-full sm:w-40 self-end mt-4">
						<div className="flex flex-col gap-3">
							{showPrintButton && permissionStore.userPermission !== PermissionRole.NOT_AUTHORIZED ? (
								<Button
									className="rounded-lg border border-gray-300 bg-white font-semibold !text-gray-700 hover:bg-gray-50"
									onClick={handlePrintModalOpen}
								>
									<FontAwesomeIcon icon={faPrint} className="mr-2" /> Print
								</Button>
							) : null}

							{!!showCompleteMatchesButton && (
								<Button onClick={handleCompleteMatchesClick} className={primaryButtonClasses}>
									Complete matches
								</Button>
							)}

							{!!showLineupsButton && (
								<Button onClick={handleLineupsNavigation} className={primaryButtonClasses}>
									Set lineups
								</Button>
							)}

							{!!showSubstituteButton && (
								<Button
									onClick={handleSubstituteNavigation}
									className="hover:bg-gray-50 bg-white border font-semibold text-sm rounded-md py-2 border-gray-300 text-center w-full"
								>
									Substitute
								</Button>
							)}
						</div>

						{isTieBreakerMatchCompleted ? (
							<Button
								onClick={scrollToTiebreaker}
								className="float-right font-semibold text-sm bg-darkorange-400 mt-3 text-white rounded-md px-2 py-1 w-full"
							>
								Go to {tieBreakerTitle}
							</Button>
						) : null}
					</div>
				) : (
					<div className="w-full sm:w-fit flex flex-col gap-3 self-end mt-4">
						<Button
							className="w-full sm:w-fit font-semibold text-sm bg-darkorange-400 hover:bg-orange-500 text-white rounded-md text-center whitespace-nowrap self-end"
							onClick={() => router.replace(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}`)}
						>
							Go to matchup
						</Button>
						{!isLineupPage &&
							(matchup.matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
								matchup.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
								matchup.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
								<DreamBreakerBox {...dreamBreakerProps} matchupData={matchup} className="bg-white" />
							)}
					</div>
				)}
			</div>
			<PrintMatchesModal open={showPrintModal} onOpenChange={setShowPrintModal} onPrint={handlePrintWithOptions} />

			<CompleteMatchesModal
				isOpen={isCompleteMatchesModalOpen}
				onClose={() => setIsCompleteMatchesModalOpen(false)}
				matchupId={matchup.uuid}
				incompleteMatches={incompleteMatches}
			/>

			<CompleteMatchupModal
				isOpen={isCompleteMatchupModalOpen}
				onClose={() => setIsCompleteMatchupModalOpen(false)}
				matchupId={matchup.uuid}
				isTie={isTie}
				tieBreakerMatchId={tieBreakerMatch?.matchUuid ?? ''}
			/>
			{isEditMatchupModalOpen ? (
				<EditMatchupStatusModal
					matchupStatus={matchup.matchupStatus}
					matchupCompletedType={matchup.matchupCompletedType}
					teamOneTitle={matchup.teamOneTitle}
					teamTwoTitle={matchup.teamTwoTitle}
					onClose={() => setIsEditMatchupModalOpen(false)}
					onConfirm={(status) => {
						updateMatchupMutation.mutate(
							{
								matchup_uuid: matchup.uuid,
								matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS),
								matchup_completed_type: status
							},
							{
								onSuccess: () => {
									setIsEditMatchupModalOpen(false);
								}
							}
						);
					}}
					onUndo={handleUndoAction}
					isSubmitting={updateMatchupMutation.isPending}
				/>
			) : null}

			{selectedPrintOptions.sheetType === 'simplified' && (
				<SimplifiedScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					teamOneTitle={matchup.teamOneTitle}
					teamTwoTitle={matchup.teamTwoTitle}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
				/>
			)}

			{selectedPrintOptions.sheetType === 'official' && (
				<OfficialScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
					podsResponse={podsResponse}
					timeZone={matchup.timezone}
					teamOneTitle={matchup.teamOneTitle}
					teamTwoTitle={matchup.teamTwoTitle}
				/>
			)}

			{selectedPrintOptions.sheetType === 'mlp' && (
				<MlpScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					teamOneTitle={matchup.teamOneTitle}
					teamTwoTitle={matchup.teamTwoTitle}
					sheetSize={'Half Sheets'} // NOTE: MLP only has half sheets, bcs is the big sheet
					rotations={rotationsInfo?.data ?? { matchUuid: '', teamOne: [], teamTwo: [] }}
				/>
			)}
		</div>
	);
};
